import React, { useState } from 'react';
import { MagnifyingGlassIcon, FunnelIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../contexts/ThemeContext';
import QRCodeRow from '../components/QRCodeRow';
import Pagination from '../components/Pagination';
import { useQRCodes } from '../hooks/useQRCodes';
import { useContent } from '../hooks/useContent';

const AnalyticsPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const { isDark } = useTheme();

  const {
    qrCodes,
    loading: isLoading,
    error,
    cachedStats: statsCache,
    currentPage,
    totalPages,
    totalItems,
    globalStatsLoading,
    loadQRCodes,
    cacheStats,
    getGlobalStats,
    goToPage
  } = useQRCodes();

  const totalStats = getGlobalStats();

  const handleStatsLoad = (qrCodeUuid, stats) => {
    cacheStats(qrCodeUuid, stats);
  };

  const handleRefresh = () => {
    loadQRCodes();
  };

  const handleSortChange = (newSortBy) => {
    if (sortBy === newSortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortOrder('desc');
    }
  };

  const sortQRCodes = (codes) => {
    return [...codes].sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'created_at':
          aValue = new Date(a.created_at);
          bValue = new Date(b.created_at);
          break;
        case 'scan_count':
          aValue = statsCache[a.uuid]?.total_visits || 0;
          bValue = statsCache[b.uuid]?.total_visits || 0;
          break;
        case 'redirect_url':
          aValue = (a.redirect_url || a.url || '').toLowerCase();
          bValue = (b.redirect_url || b.url || '').toLowerCase();
          break;
        default:
          return 0;
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });
  };

  const filteredAndSortedQRCodes = sortQRCodes(
    qrCodes.filter(qrCode => {
      if (!searchTerm) return true;

      const searchLower = searchTerm.toLowerCase();
      return (
        (qrCode.redirect_url || '').toLowerCase().includes(searchLower) ||
        (qrCode.url || '').toLowerCase().includes(searchLower) ||
        (qrCode.uuid || '').toLowerCase().includes(searchLower) ||
        (qrCode.type || '').toLowerCase().includes(searchLower)
      );
    })
  );

  const { content, loading: contentLoading } = useContent('analyticsPage');

  if (contentLoading || !content) {

    return (
      <div className={`pt-16 min-h-screen flex items-center justify-center ${isDark ? 'bg-dark-900' : 'bg-gray-50'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${
      isDark ? '' : 'bg-gray-50'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-8">
        <div className="mb-6 md:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className={`text-2xl md:text-3xl font-bold ${
                isDark ? 'text-white' : 'text-gray-900'
              }`}>{content.pageTitle}</h1>
              <p className={`text-sm md:text-base ${
                isDark ? 'text-gray-400' : 'text-gray-600'
              }`}>
	  	{content.pageSubtitle}
              </p>
            </div>
            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 text-white rounded-lg transition-colors"
            >
              <ArrowPathIcon className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              <span>{content.refreshButton}</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
          <div className={`border rounded-xl p-4 md:p-6 ${
            isDark
              ? 'bg-dark-800 border-dark-700'
              : 'bg-white border-gray-200 shadow-sm'
          }`}>
            <p className={`text-sm ${
              isDark ? 'text-gray-400' : 'text-gray-600'
            }`}>{content.statsCards.totalCreated.label}</p>
            <p className={`text-2xl md:text-3xl font-bold ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>{totalStats.totalQRCodes}</p>
          </div>
          <div className={`border rounded-xl p-4 md:p-6 ${
            isDark
              ? 'bg-dark-800 border-dark-700'
              : 'bg-white border-gray-200 shadow-sm'
          }`}>
            <p className={`text-sm ${
              isDark ? 'text-gray-400' : 'text-gray-600'
            }`}>{content.statsCards.totalActive.label}</p>
            <p className={`text-2xl md:text-3xl font-bold ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>{totalStats.activeQRCodes}</p>
          </div>
          <div className={`border rounded-xl p-4 md:p-6 ${
            isDark
              ? 'bg-dark-800 border-dark-700'
              : 'bg-white border-gray-200 shadow-sm'
          }`}>
            <p className={`text-sm ${
              isDark ? 'text-gray-400' : 'text-gray-600'
            }`}>{content.statsCards.totalScans.label}</p>
            <div className="flex items-center space-x-2">
              <p className={`text-2xl md:text-3xl font-bold ${
                isDark ? 'text-white' : 'text-gray-900'
              }`}>{totalStats.totalScans.toLocaleString()}</p>
              {globalStatsLoading && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
              )}
            </div>
          </div>
          <div className={`border rounded-xl p-4 md:p-6 ${
            isDark
              ? 'bg-dark-800 border-dark-700'
              : 'bg-white border-gray-200 shadow-sm'
          }`}>
            <p className={`text-sm ${
              isDark ? 'text-gray-400' : 'text-gray-600'
            }`}>{content.statsCards.weeklyScans.label}</p>
            <div className="flex items-center space-x-2">
              <p className={`text-2xl md:text-3xl font-bold ${
                isDark ? 'text-white' : 'text-gray-900'
              }`}>{totalStats.weeklyScans.toLocaleString()}</p>
              {globalStatsLoading && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
              )}
            </div>
          </div>
        </div>

        <div className={`border rounded-xl p-4 md:p-6 mb-6 ${
          isDark
            ? 'bg-dark-800 border-dark-700'
            : 'bg-white border-gray-200 shadow-sm'
        }`}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0 sm:space-x-4">
            <div className="relative flex-1 max-w-md">
              <MagnifyingGlassIcon className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${
                isDark ? 'text-gray-400' : 'text-gray-500'
              }`} />
              <input
                type="text"
                placeholder={content.searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDark
                    ? 'bg-dark-700 border-dark-600 text-white placeholder-gray-400'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                }`}
              />
            </div>

            <div className="flex items-center space-x-2">
              <FunnelIcon className={`w-4 h-4 ${
                isDark ? 'text-gray-400' : 'text-gray-500'
              }`} />
              <select
                value={`${sortBy}-${sortOrder}`}
                onChange={(e) => {
                  const [newSortBy, newSortOrder] = e.target.value.split('-');
                  setSortBy(newSortBy);
                  setSortOrder(newSortOrder);
                }}
                className={`border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDark
                    ? 'bg-dark-700 border-dark-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              >
                <option value="created_at-desc">{content.sortOptions.newest}</option>
                <option value="created_at-asc">{content.sortOptions.oldest}</option>
                <option value="scan_count-desc">{content.sortOptions.mostScanned}</option>
                <option value="scan_count-asc">{content.sortOptions.leastScanned}</option>
              </select>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {isLoading ? (
            <div className={`border rounded-xl p-8 text-center ${
              isDark
                ? 'bg-dark-800 border-dark-700'
                : 'bg-white border-gray-200 shadow-sm'
            }`}>
              <div className={`inline-flex items-center space-x-2 ${
                isDark ? 'text-gray-400' : 'text-gray-600'
              }`}>
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                <span>{content.loadingMessage}</span>
              </div>
            </div>
          ) : error ? (
            <div className={`border rounded-xl p-8 text-center ${
              isDark
                ? 'bg-dark-800 border-red-700'
                : 'bg-red-50 border-red-200'
            }`}>
              <p className={isDark ? 'text-red-400' : 'text-red-600'}>{error}</p>
              <button
                onClick={handleRefresh}
                className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              >
		  {content.retryButton}
              </button>
            </div>
          ) : filteredAndSortedQRCodes.length === 0 ? (
            <div className={`border rounded-xl p-8 text-center ${
              isDark
                ? 'bg-dark-800 border-dark-700'
                : 'bg-white border-gray-200 shadow-sm'
            }`}>
              <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                {searchTerm ? content.emptySearch : content.emptyState }
              </p>
            </div>
          ) : (
            <>
              <div className={`hidden md:block border rounded-lg p-4 ${
                isDark
                  ? 'bg-dark-750 border-dark-700'
                  : 'bg-gray-50 border-gray-200'
              }`}>
                <div className={`grid grid-cols-4 gap-4 text-xs font-medium uppercase tracking-wider ${
                  isDark ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  <div className="col-span-2">
                    <button
                      onClick={() => handleSortChange('redirect_url')}
                      className={`transition-colors ${
                        isDark ? 'hover:text-white' : 'hover:text-gray-900'
                      }`}
                    >
		  	{content.tableHeaders.redirectUrl}
                    </button>
                  </div>
                  <div>
                    <button
                      onClick={() => handleSortChange('created_at')}
                      className={`transition-colors ${
                        isDark ? 'hover:text-white' : 'hover:text-gray-900'
                      }`}
                    >
			  {content.tableHeaders.createdAt}
                    </button>
                  </div>
                  <div>
                    <button
                      onClick={() => handleSortChange('scan_count')}
                      className={`transition-colors ${
                        isDark ? 'hover:text-white' : 'hover:text-gray-900'
                      }`}
                    >
			  {content.tableHeaders.scanCount}
                    </button>
                  </div>
                </div>
              </div>

              {filteredAndSortedQRCodes.map((qrCode) => (
                <QRCodeRow
                  key={qrCode.uuid}
                  qrCode={qrCode}
                  onStatsLoad={handleStatsLoad}
                  cachedStats={statsCache[qrCode.uuid]}
                />
              ))}
            </>
          )}
        </div>

        {!isLoading && !error && filteredAndSortedQRCodes.length > 0 && (
          <div className="mt-6">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              itemsPerPage={20}
              currentItemsCount={filteredAndSortedQRCodes.length}
              onPageChange={goToPage}
              loading={isLoading}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsPage;
